// src/config/env.ts - Environment configuration
// For Vite, use import.meta.env for client-side variables
// Variables must be prefixed with VITE_ to be exposed to the client

/// <reference types="vite/client" />

// Type-safe access to import.meta.env
const getEnvVar = (key: string, defaultValue: string): string => {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
        return (import.meta.env as Record<string, string | undefined>)[key] || defaultValue;
    }
    return defaultValue;
};

const isDevMode = (): boolean => {
    // Prefer Vite-provided flags; fall back to MODE for older tooling
    const env = (typeof import.meta !== 'undefined' && import.meta.env) ? import.meta.env : undefined;
    if (env && typeof env.DEV === 'boolean') return env.DEV;
    return (env as unknown as { MODE?: string } | undefined)?.MODE === 'development';
};

const parseBool = (value: string | undefined, defaultValue = false): boolean => {
    if (value == null) return defaultValue;
    const v = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'y', 'on'].includes(v)) return true;
    if (['0', 'false', 'no', 'n', 'off'].includes(v)) return false;
    return defaultValue;
};

function getDefaultApiBaseUrl(): string {
    if (typeof window !== 'undefined' && window.location?.origin) {
        return window.location.origin;
    }
    return 'http://localhost:36236';
}

let cachedApiBaseUrl: string | undefined;

/** Resolves API host at call time so production builds work without a baked-in localhost URL. */
export function getCloudPrepperBaseUrl(): string {
    if (cachedApiBaseUrl) return cachedApiBaseUrl;

    const explicit =
        getEnvVar('VITE_CLOUD_PREPPER_BASE_URL', '') ||
        getEnvVar('VITE_API_URL', '');
    if (explicit) {
        cachedApiBaseUrl = explicit;
        return cachedApiBaseUrl;
    }

    cachedApiBaseUrl = getDefaultApiBaseUrl();
    return cachedApiBaseUrl;
}

export const CLOUD_PREPPER_GET_QUESTIONS = 
    getEnvVar('VITE_CLOUD_PREPPER_GET_QUESTIONS', '/getExamQuestions');

export const CLOUD_PREPPER_ADD_QUESTION = 
    getEnvVar('VITE_CLOUD_PREPPER_ADD_QUESTION', '/api/questions/addQuestion');

export const CLOUD_PREPPER_UPDATE_QUESTION = 
    getEnvVar('VITE_CLOUD_PREPPER_UPDATE_QUESTION', '/updateQuestion');

export const CLOUD_PREPPER_BACKUP_STATUS = 
    getEnvVar('VITE_CLOUD_PREPPER_BACKUP_STATUS', '/api/backup/status');

export const CLOUD_PREPPER_BACKUP_DASHBOARD = 
    getEnvVar('VITE_CLOUD_PREPPER_BACKUP_DASHBOARD', '/api/backup/dashboard');

export const CLOUD_PREPPER_BACKUP_LIST = 
    getEnvVar('VITE_CLOUD_PREPPER_BACKUP_LIST', '/api/backup/list');

export const CLOUD_PREPPER_BACKUP_GENERATE = 
    getEnvVar('VITE_CLOUD_PREPPER_BACKUP_GENERATE', '/api/backup/generate');

export const CLOUD_PREPPER_BACKUP_DOWNLOAD = 
    getEnvVar('VITE_CLOUD_PREPPER_BACKUP_DOWNLOAD', '/api/backup/download');

export const CLOUD_PREPPER_BACKUP_RESTORE = 
    getEnvVar('VITE_CLOUD_PREPPER_BACKUP_RESTORE', '/api/backup/restore');

/**
 * Dev-only auth bypass.
 * Enable locally via `.env`:
 * - VITE_DEV_AUTH_BYPASS=true
 * - VITE_DEV_AUTH_TOKEN=... (optional; used if backend still expects a bearer token)
 * - VITE_DEV_AUTH_ROLE=admin|user (optional; default admin for local dev)
 */
export const DEV_AUTH_BYPASS_ENABLED: boolean =
    isDevMode() && parseBool((import.meta.env as Record<string, string | undefined>)?.VITE_DEV_AUTH_BYPASS, false);

export const DEV_AUTH_BYPASS_TOKEN: string =
    getEnvVar('VITE_DEV_AUTH_TOKEN', '');

export const DEV_AUTH_BYPASS_ROLE: 'admin' | 'user' =
    (getEnvVar('VITE_DEV_AUTH_ROLE', 'admin') === 'user' ? 'user' : 'admin');

// Stripe
export const STRIPE_PUBLISHABLE_KEY = getEnvVar('VITE_STRIPE_PUBLISHABLE_KEY', '');
